<!--
// app/pages/[region]/Mercato.vue
-->
<script setup lang="ts">
import type { MercatoPageContent } from "~/src/content/domain/models/MercatoPage";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import postUiService from "~/src/post/infrastructure/ui-services/postUiService";
import HeaderTitleCentered from "~/app/components/Shared/HeaderTitleCentered.vue";
import CardNewsArticle from "~/app/components/Shared/news/CardNewsArticle.vue";
import SliderArrowsBehaviour from "~/app/components/Shared/slider-matches/SliderArrowsBehaviour.vue";
import PrimeDataTable from "primevue/datatable";
import PrimeColumn from "primevue/column";
import CardNoDataMessage from "~/app/components/Shared/CardNoDataMessage.vue";
import FaqItem from "~/app/components/pages/region-faqs/FaqItem.vue";
import { setAliasUrl } from "~/src/Shared/utils";
import { useTeams } from "~/app/composables/teams";
import { useWebContext } from "~/app/composables/webContext";

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/mercato-transfers",
    es: "/[region]/mercato-fichajes",
    de: "/[region]/mercato-transfers",
    fr: "/[region]/mercato-transferts",
    it: "/[region]/mercato",
    pt: "/[region]/mercato-transferencias",
  },
  locales: ["en", "es"],
});

definePageMeta({
  middleware: [
    "not-found-world-cup",
    "not-found-nations",
    "not-found-king-of-kings-nations",
    "not-found-brazil",
    "not-found-italy",
    "not-found-americas",
    "not-found-france",
    "not-found-germany",
  ],
});

const { getPosts } = postUiService();
const { locale } = useI18n();
const { currentSeasonTeams } = useCompetitionStore();
const { teamArrToObjById } = useTeams();
const { isQueens, contextSiteTitle } = useWebContext();

const fetchedContent = await useFetch("/api/cms/1/mercato", {
  query: { page_category: isQueens ? 341 : 340, lang: locale.value },
});
if (fetchedContent.error.value || !fetchedContent.data.value) console.error(fetchedContent.error.value);

const content: MercatoPageContent | undefined = fetchedContent.data.value
  ? fetchedContent.data.value
  : undefined;

const selectedTeam = ref<number | undefined>();
const schemaQuestionsAnswers = ref();

const postsOpts = {
  params: {
    per_page: 20,
    page: 1,
    lang: locale.value,
    tags: 214,
  },
};

const mercatoPosts = await getPosts(postsOpts);
const activeTeams = await teamArrToObjById(currentSeasonTeams.filter(t => !t.isPlaceholder));

const transfersData = computed(() => {
  return selectedTeam.value
    ? content?.transfers_section.transfers_table.filter(
        p => Number(p.team_destination) === selectedTeam.value || Number(p.team_origin) === selectedTeam.value
      )
    : content?.transfers_section.transfers_table;
});

const formatDate = date => {
  const any = date.substring(0, 4);
  const mes = date.substring(4, 6);
  const dia = date.substring(6, 8);

  return `${dia}/${mes}/${any}`;
};

const createFaqSchema = (): string[] => {
  const allQuestions = [];
  if (content) {
    for (const faq of content.faqs_section.questions_answers) {
      allQuestions.push(
        `{"@type": "Question", "name": "${faq.question_title}", "acceptedAnswer": {"@type": "Answer","text": "${faq.answer_content}"}}`
      );
    }
  }

  return allQuestions;
};

useSeoMeta({
  title: () => content?.page_title || `Mercato ${contextSiteTitle} 2025`,
  ogTitle: () => content?.page_title || `Mercato ${contextSiteTitle} 2025`,
  description: () => content?.page_subtitle || `Mercato ${contextSiteTitle} 2025`,
  ogDescription: () => content?.page_subtitle || `Mercato ${contextSiteTitle} 2025`,
});

schemaQuestionsAnswers.value = createFaqSchema();

if (!!schemaQuestionsAnswers.value) {
  useHead({
    script: [
      {
        type: "application/ld+json",
        innerHTML: `{"@context": "https://schema.org","@type": "FAQPage","mainEntity": [${schemaQuestionsAnswers.value}]}`,
        tagPosition: "head",
        tagPriority: "low",
        key: "schemaQuestions",
      },
    ],
  });
}
</script>

<template>
  <header-title-centered
    v-if="!!content"
    :title="content.page_title"
    :subtitle="content.page_subtitle"
    is-mercato-landing
  ></header-title-centered>

  <div class="kql-mercato" v-if="!!content" :key="locale">
    <p class="text-xl md:text-2xl text-center text-gray-300">{{ content.mercato_dates_text }}</p>

    <section v-if="!!mercatoPosts && !!mercatoPosts.length">
      <div>
        <h2>{{ content.last_news_section.last_news_section_title }}</h2>
        <p v-if="content.last_news_section.last_news_section_subtitle" class="subtitle">
          {{ content.last_news_section.last_news_section_subtitle }}
        </p>

        <slider-arrows-behaviour
          slider-container-id="postsSlider"
          :conditionally-render-if="!!mercatoPosts && !!mercatoPosts.length"
          button-left-id="buttonLeft"
          button-right-id="buttonRight"
          :button-scroll-distance="360"
        >
          <div id="postsSlider" class="hide-scrollbar posts-slider-wrapper">
            <card-news-article v-for="post in mercatoPosts" :post="post" is-post-slider></card-news-article>
          </div>
        </slider-arrows-behaviour>
      </div>
    </section>

    <div :class="{ 'bg-gradient-to-b from-primary-dark to-black': !!mercatoPosts && !!mercatoPosts.length }">
      <section v-if="content.rules_section.rules_section_title" class="!mt-0 pt-20">
        <div>
          <h2>{{ content.rules_section.rules_section_title }}</h2>
          <p v-if="content.rules_section.rules_section_subtitle" class="subtitle">
            {{ content.rules_section.rules_section_subtitle }}
          </p>

          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-10 gap-y-4 mt-2">
            <div v-for="day in content.rules_section.rules_days">
              <h3>{{ day.rule_day_title }}</h3>
              <div
                v-for="window in day.mercato_window"
                class="bg-gray-900 mt-3 mb-4 p-2 md:p-4 rounded-lg text-center"
              >
                <h4 class="text-gray-400 uppercase !text-sm !font-normal">{{ window.window_title }}</h4>
                <hr />
                <p class="mt-4">{{ window.window_description }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-if="content.additional_rules_section.additional_rules_section_title">
        <div>
          <h2>{{ content.additional_rules_section.additional_rules_section_title }}</h2>
          <p v-if="content.additional_rules_section.additional_rules_section_subtitle" class="subtitle">
            {{ content.additional_rules_section.additional_rules_section_subtitle }}
          </p>

          <ul class="max-w-4xl mx-auto my-6">
            <li v-for="rule in content.additional_rules_section.additional_rules_list">
              {{ rule.additional_rule_text }}
            </li>
          </ul>
        </div>
      </section>
    </div>

    <section v-if="content.mercato_simulator_section.mercato_simulator_section_title" class="px-2">
      <div class="bg-gray-800 rounded-lg py-10 px-4">
        <h2>{{ content.mercato_simulator_section.mercato_simulator_section_title }}</h2>
        <p v-if="content.mercato_simulator_section.mercato_simulator_section_subtitle" class="subtitle">
          {{ content.mercato_simulator_section.mercato_simulator_section_subtitle }}
        </p>

        <div class="mt-10 btn btn-emphasis max-w-max mx-auto text-center">
          <a :href="content.mercato_simulator_section.simulator_link" target="_self" class="">{{
            content.mercato_simulator_section.simulator_button_text
          }}</a>
        </div>
      </div>
    </section>

    <section v-if="content.transfers_section.transfers_section_title">
      <div>
        <h2>{{ content.transfers_section.transfers_section_title }}</h2>
        <p v-if="content.transfers_section.transfers_section_subtitle" class="subtitle">
          {{ content.transfers_section.transfers_section_subtitle }}
        </p>

        <div v-if="(selectedTeam || selectedTeam === undefined) && !!transfersData" class="mt-6 px-2 md:px-4">
          <p class="text-sm font-bold">{{ $t("mercato.filterByTeam.sentence") }}</p>
          <div class="flex flex-wrap justify-center md:justify-between items-center gap-4 md:gap-6 mt-2 mb-5">
            <button
              @click="selectedTeam = undefined"
              class="w-10 h-10 text-xs md:w-12 md:h-12 flex flex-col justify-center items-center bg-gray-800 rounded-full md:text-sm p-3"
              :class="{ 'border border-primary': !selectedTeam }"
            >
              {{ $t("general.all.male") }}
            </button>
            <button
              v-for="team in activeTeams"
              :key="team.id"
              @click="selectedTeam = team.id"
              class="w-11 h-11 md:w-14 md:h-14 flex flex-col justify-center items-center rounded-full"
              :class="{ 'border border-primary p-0.5': selectedTeam === team.id }"
            >
              <img :src="team.logo.url" :alt="team.completeName" width="56" height="56" />
            </button>
          </div>
        </div>

        <div v-if="transfersData && transfersData.length" class="stats-table-data max-w-full overflow-hidden">
          <PrimeDataTable :value="transfersData" scrollable removableSort scrollHeight="540px">
            <PrimeColumn
              field="date"
              :header="$t('matches.turn.date')"
              class="leading-tight text-center"
              style="max-width: 150px; min-width: 100px"
              sortable
            >
              <template #body="slotProps"
                ><span v-if="slotProps && slotProps.data">{{
                  formatDate(slotProps.data.date)
                }}</span></template
              >
            </PrimeColumn>
            <PrimeColumn
              field="player_name"
              :header="$t('general.player.word')"
              class="leading-tight !text-center"
              style="max-width: 180px; min-width: 110px"
              ><template #body="slotProps"
                ><a
                  v-if="slotProps && slotProps.data"
                  :href="slotProps.data.player_page_link"
                  class="underline"
                  >{{ slotProps.data.player_name }}</a
                ></template
              ></PrimeColumn
            >
            <PrimeColumn
              field="team_origin"
              :header="$t('mercato.teamOrigin')"
              class="team text-center leading-none"
              style="max-width: 150px; min-width: 90px"
            >
              <template #body="slotProps">
                <div class="flex items-center justify-center w-full">
                  <NuxtImg
                    v-if="slotProps && slotProps.data?.team_origin"
                    loading="lazy"
                    :src="setAliasUrl(activeTeams[slotProps.data.team_origin].logo.url)"
                    :alt="activeTeams[slotProps.data.team_origin].completeName"
                    class="team-logo"
                    width="48"
                    height="48"
                    sizes="40px sm:48px"
                  />
                </div>
              </template>
            </PrimeColumn>
            <PrimeColumn
              field="team_destination"
              :header="$t('mercato.teamDestination')"
              class="team text-center leading-none"
              style="max-width: 150px; min-width: 90px"
            >
              <template #body="slotProps">
                <div class="flex items-center justify-center w-full">
                  <NuxtImg
                    v-if="slotProps && slotProps.data?.team_destination"
                    loading="lazy"
                    :src="setAliasUrl(activeTeams[slotProps.data.team_destination].logo.url)"
                    :alt="activeTeams[slotProps.data.team_destination].completeName"
                    class="team-logo"
                    width="48"
                    height="48"
                    sizes="40px sm:48px"
                  />
                </div>
              </template>
            </PrimeColumn>
          </PrimeDataTable>
        </div>
        <div v-else class="mt-6">
          <card-no-data-message
            :message="
              !selectedTeam && !transfersData
                ? $t('shared.errorMessage.noData')
                : $t('mercato.noRelatedTransfers.sentence')
            "
          ></card-no-data-message>
        </div>
      </div>
    </section>

    <section v-if="content.faqs_section.faqs_section_title">
      <div>
        <h2>{{ content.faqs_section.faqs_section_title }}</h2>
        <p v-if="content.faqs_section.faqs_section_subtitle" class="subtitle">
          {{ content.faqs_section.faqs_section_subtitle }}
        </p>

        <div id="faqs-accordion" class="max-w-2xl mx-auto">
          <faq-item
            v-for="(faq, index) in content.faqs_section.questions_answers"
            :key="index"
            :title="faq.question_title"
            :content="faq.answer_content"
          >
          </faq-item>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.posts-slider-wrapper {
  @apply my-6 w-full max-w-full overflow-x-auto flex flex-nowrap items-stretch justify-start md:gap-2;
}

.kql-mercato p.subtitle {
  @apply !my-1 text-center text-gray-400 text-lg;
}

.kql-mercato h2,
.kql-mercato h3,
.kql-mercato h4 {
  @apply text-center;
}

.kql-mercato section {
  @apply mb-20 mt-20 md:mb-32;
}

.kql-mercato section > div {
  @apply max-w-7xl mx-auto px-4 md:px-0;
}
</style>
